/**
 * Tools for coordinated resizing of meshes and their UVs.
 */

import { Box3, BufferGeometry, Mesh, Object3D } from "three"

type TypedArray =
  | Int8Array
  | Uint8Array
  | Uint8ClampedArray
  | Int16Array
  | Uint16Array
  | Int32Array
  | Uint32Array
  | Float32Array
  | Float64Array;

export enum Axis {
    x = 0,
    y = 1,
    z = 2,
}

export enum UVAxis {
    u = 0,
    v = 1,
}

/**
 * This can be used to translate the vertexes of an individual geometry so that
 * its origin point is moved relative to the bounding box of the geometry.
 *
 * Each of the axis parameters is a number on the interval [0, 1] interpreted
 * relative to the measured extent of the mesh along each axis. So setting
 * x, y, and z to 0, 0, and 0 signifies placing the origin at the left, bottom,
 * back of the object, and 1, 1, and 1 signifies the right, top, front.
 *
 * A similar function is available in view3d/set-origin.ts that operates on
 * objects.
 */
export const setOrigin = (geo: BufferGeometry, x: number, y: number, z: number) =>
{
    if(geo.boundingBox == null) geo.computeBoundingBox();
    const
        b = geo.boundingBox,
        width = b.max.x - b.min.x,
        height = b.max.y - b.min.y,
        depth = b.max.z - b.min.z,
        dx = -x * width - b.min.x,
        dy = -y * height - b.min.y,
        dz = -z * depth - b.min.z;
    geo.translate(dx, dy, dz);
}

/**
 * Calculates the perimeters of all the triangles in the mesh, both in 3D and
 * UV space and returns the quotient UV/3D, i.e. how many UV units per unit in
 * 3D space. This scale factor allows you to move UV coordinates relative to
 * some movement in 3D, maintaining the UV mapping's aspect ratio when they are
 * axis-aligned.
 * @param skip - For meshes with many triangles, computing this scaling factor
 * could take too much time, so you could calculate the value for every Nth
 * triangle, and rely on it averaging out. This parameter is the number of
 * triangles to skip between iterations, so skipping 1 means you calculate the
 * perimeter for half of the triangles in the mesh, and skipping 2 means only
 * using a third of the triangles.
 */
export const calculateUvScale = (geo: BufferGeometry, skip: number = 0): number => {
    const
        index = geo.index.array,
        position = geo.getAttribute('position').array,
        uvs = geo.getAttribute('uv').array,
        inc = 3 * (skip + 1);
    let
        pTotal = 0,
        uvTotal = 0;
    for(let i = 0; i < index.length; i += inc)
    {
        const 
            a = index[i] * 3,
            ax = position[a], ay = position[a+1], az = position[a+2],
            auv = index[i] * 2,
            au = uvs[auv], av = uvs[auv+1],

            bi = i+1,
            b = index[bi] * 3,
            bx = position[b], by = position[b+1], bz = position[b+2],
            buv = index[bi] * 2,
            bu = uvs[buv], bv = uvs[buv+1],

            ci = i+2,
            c = index[ci] * 3,
            cx = position[c], cy = position[c+1], cz = position[c+2],
            cuv = index[ci] * 2,
            cu = uvs[cuv], cv = uvs[cuv+1];

        pTotal += 
                Math.sqrt((ax - bx)**2 + (ay - by)**2 + (az - bz)**2)
              + Math.sqrt((bx - cx)**2 + (by - cy)**2 + (bz - cz)**2)
              + Math.sqrt((cx - ax)**2 + (cy - ay)**2 + (cz - az)**2)
        uvTotal +=
                Math.sqrt((au - bu)**2 + (av - bv)**2)
              + Math.sqrt((bu - cu)**2 + (bv - cv)**2)
              + Math.sqrt((cu - au)**2 + (cv - av)**2);
    }
    return uvTotal / pTotal;
}



/**
 * This procedure moves verticies on the positive side of a given axis and
 * applies a coordinated movement to the corresponding UVs. This provides a way
 * to "scale" rectilinear meshes representing things like cabinets, doors,
 * shelves, etc., without distorting the aspect ratio of their parts or
 * stretching their texture maps.
 * @param d - the offset to apply to the vertices on the positive side of the
 * given axis.
 * @param axis - which axis to apply this transform to, i.e. X, Y, or Z.
 */
export const resize = (geo: BufferGeometry, d: number, axis: Axis) => {
    const
        position = geo.getAttribute('position').array as TypedArray,
        uvs = geo.getAttribute('uv').array as TypedArray,
        color = geo.getAttribute('color').array as TypedArray,
        offset = d * calculateUvScale(geo);
    for(let i = 0, j = 0, k = 0; i < position.length; i += 3, j += 2, k += 4)
    {
        const pi = i + axis as number;
        if(position[pi] > 0) 
        {
            position[pi] += d;
            const c = color[k + axis as number];
            if(c == 0.5) {
                // move the vertex on the U axis
                uvs[j] += offset;
            } else if(c == 1.0) {
                // move the vertex on the V axis, and invert the direction.
                // This may need to be changed to += if we use glb native
                // materials.
                uvs[j+1] -= offset;
            }
        }
    }
}


/**
 * It can be difficult to get the size of a GLB model because each of its
 * materials belong to a different mesh. This gets the union of all the
 * geometries' bounding boxes and places the result in `box`. Note that this
 * will not account for any rotation/scaling of objects. It is probably only
 * appropriate for the GLB use case mentioned above.
 */
export const getGeometrySize = (obj: Object3D, box: Box3) => {
    box.min.set(0, 0, 0);
    box.max.set(0, 0, 0);
    obj.traverse(obj => {
        if(obj instanceof Mesh) {
            if(obj.geometry.boundingBox == null) {
                obj.geometry.computeBoundingBox();
            }
            box.union(obj.geometry.boundingBox);
        }
    });
}
